// Shared ownership for application dialogs. SweetAlert owns its own keyboard
// handling while its non-toast popup is present; application dialogs suspend.
const stack = [];
let observer, originalOverflow, originalTicketClass, restoreTarget, externalDialog;
let nextTitleId = 0;
const focusSelector = 'a[href],button,input:not([type="hidden"]),select,textarea,[contenteditable="true"],[tabindex]';

function visible(element) {
  if (!element || !document.documentElement.contains(element)) return false;
  for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (node.hidden || node.hasAttribute('inert') || style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}
function usable(element) {
  return visible(element) && !element.disabled && element.getAttribute('aria-disabled') !== 'true';
}
function focusables(element) {
  const controls = Array.from(element.querySelectorAll(focusSelector)).filter(node => usable(node) && node.tabIndex >= 0);
  return controls.filter(node => {
    if (!node.matches('input[type="radio"]') || !node.name) return true;
    const group = controls.filter(other => other.matches('input[type="radio"]') && other.name === node.name && other.form === node.form);
    return node === (group.find(other => other.checked) || group[0]);
  }).sort((a, b) => (a.tabIndex > 0 ? a.tabIndex : Infinity) - (b.tabIndex > 0 ? b.tabIndex : Infinity));
}
function sweetAlert() {
  return Array.from(document.querySelectorAll('.swal2-popup:not(.swal2-toast)')).find(visible);
}
function top() { return stack[stack.length - 1]; }
function setAttribute(element, name, value) {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}
function rememberAttribute(entry, element, name) {
  if (!entry.attributes.some(item => item.element === element && item.name === name)) {
    entry.attributes.push({ element, name, value: element.getAttribute(name) });
  }
}
function nameDialog(entry) {
  const element = entry.element;
  const name = typeof entry.options.name === 'function' ? entry.options.name() : entry.options.name;
  if (name) {
    rememberAttribute(entry, element, 'aria-label');
    setAttribute(element, 'aria-label', name);
  } else if (!element.getAttribute('aria-label') && !element.getAttribute('aria-labelledby')) {
    const title = Array.from(element.querySelectorAll('h1,h2,h3,h4,h5,h6')).find(node => node.textContent.trim());
    if (title) {
      rememberAttribute(entry, title, 'id');
      if (!title.id) title.id = 'app-dialog-title-' + (++nextTitleId);
      rememberAttribute(entry, element, 'aria-labelledby');
      setAttribute(element, 'aria-labelledby', title.id);
    } else {
      rememberAttribute(entry, element, 'aria-label');
      setAttribute(element, 'aria-label', entry.options.fallbackName || 'Dialog');
    }
  }
}
function focusEntry(entry) {
  if (!entry || !visible(entry.element) || (entry.options.ready && !entry.options.ready())) return;
  const initial = typeof entry.options.initialFocus === 'function' ? entry.options.initialFocus() : null;
  const controls = focusables(entry.element);
  const field = controls.find(node => node.matches('input,select,textarea,[autofocus]'));
  const target = usable(initial) ? initial : field || controls[0] || entry.element;
  target.focus({ preventScroll: true });
  entry.needsFocus = false;
}
function handleKeydown(event) {
  const entry = top();
  if (!entry || sweetAlert()) return;
  if (event.key === 'Escape' || event.keyCode === 27) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (entry.options.close) entry.options.close(event);
  } else if ((event.key === 'Tab' || event.keyCode === 9) && entry.options.modal !== false) {
    const controls = focusables(entry.element);
    const first = controls[0], last = controls[controls.length - 1];
    const active = document.activeElement;
    if (!controls.length || !entry.element.contains(active) || active === entry.element ||
        (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
      event.preventDefault();
      (event.shiftKey ? last : first || entry.element)?.focus();
      if (!controls.length) entry.element.focus();
    }
  }
}
function handleFocus(event) {
  const entry = top();
  if (entry && entry.element.contains(event.target)) entry.lastFocus = event.target;
  if (entry && entry.options.modal !== false && !sweetAlert() && !entry.element.contains(event.target)) focusEntry(entry);
}
function syncLayers() {
  stack.forEach((entry, index) => {
    const nodes = entry.options.layers ? entry.options.layers() : [entry.element];
    nodes.filter(Boolean).forEach(node => {
      if (!entry.layers.some(layer => layer.node === node)) {
        entry.layers.push({ node, value: node.style.zIndex, base: parseInt(window.getComputedStyle(node).zIndex, 10) || 0 });
      }
    });
    const prior = stack.slice(0, index).filter(parent => parent.options.modal !== false && !parent.element.contains(entry.element));
    const previousZ = Math.max(-1, ...prior.map(parent => parent.layerZ || 0));
    const main = entry.layers[0];
    entry.layerZ = main ? Math.max(main.base, previousZ + 2) : 0;
    entry.layers.forEach((layer, layerIndex) => {
      const value = entry.options.modal !== false && previousZ >= (main?.base || 0)
        ? String(entry.layerZ - (layerIndex ? 1 : 0)) : layer.value;
      if (layer.node.style.zIndex !== value) layer.node.style.zIndex = value;
    });
  });
}
function restoreFocus() {
  if (!restoreTarget || sweetAlert()) return;
  const entry = top();
  if (usable(restoreTarget) && (!entry || entry.options.modal === false || entry.element.contains(restoreTarget))) {
    restoreTarget.focus({ preventScroll: true });
  } else if (entry) focusEntry(entry);
  restoreTarget = null;
}
function sync() {
  const external = sweetAlert();
  if (externalDialog && !external && !restoreTarget) restoreTarget = top()?.lastFocus;
  externalDialog = external;
  const modal = stack.some(entry => entry.options.modal !== false);
  if (modal || (external && originalOverflow !== undefined)) {
    if (originalOverflow === undefined) {
      originalOverflow = document.body.style.overflow;
      originalTicketClass = document.body.classList.contains('ticket-detail-modal-open');
    }
    if (document.body.style.overflow !== 'hidden') document.body.style.overflow = 'hidden';
  } else if (originalOverflow !== undefined) {
    document.body.style.overflow = originalOverflow;
    document.body.classList.toggle('ticket-detail-modal-open', originalTicketClass);
    originalOverflow = undefined;
  }
  if (originalOverflow !== undefined) {
    const ticketOpen = originalTicketClass || stack.some(entry => entry.options.ticket);
    if (document.body.classList.contains('ticket-detail-modal-open') !== ticketOpen) document.body.classList.toggle('ticket-detail-modal-open', ticketOpen);
  }
  stack.forEach(entry => {
    nameDialog(entry);
    setAttribute(entry.element, 'aria-modal', entry === top() && !external && entry.options.modal !== false ? 'true' : 'false');
  });
  syncLayers();
  restoreFocus();
  const entry = top();
  if (!external && entry && (entry.needsFocus || (entry.options.modal !== false &&
      (!entry.element.contains(document.activeElement) || !usable(document.activeElement))))) focusEntry(entry);
  if (!stack.length && !external && observer) {
    observer.disconnect(); observer = null;
    document.removeEventListener('keydown', handleKeydown, true);
    document.removeEventListener('focus', handleFocus, true);
  }
}

export function registerDialog(element, options = {}) {
  const entry = { element, options, opener: document.activeElement, attributes: [], layers: [], needsFocus: true };
  ['role', 'tabindex', 'aria-modal'].forEach(name => rememberAttribute(entry, element, name));
  setAttribute(element, 'role', 'dialog');
  if (!element.hasAttribute('tabindex')) setAttribute(element, 'tabindex', '-1');
  stack.push(entry);
  if (!observer) {
    document.addEventListener('keydown', handleKeydown, true);
    document.addEventListener('focus', handleFocus, true);
    if (window.MutationObserver) {
      observer = new window.MutationObserver(sync);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
    } else {
      // Legacy DOM environments: poll only while a dialog is registered.
      const timer = setInterval(sync, 50);
      observer = { disconnect: () => clearInterval(timer) };
    }
  }
  sync();
  return {
    isTop: () => top() === entry && !sweetAlert(),
    refresh: sync,
    update(next) { entry.options = next; sync(); },
    release() {
      const index = stack.indexOf(entry);
      if (index < 0) return;
      const wasTop = top() === entry;
      stack.slice(index + 1).forEach(child => {
        if (entry.element.contains(child.opener)) child.opener = entry.opener;
      });
      stack.splice(index, 1);
      entry.attributes.forEach(({ element: node, name, value }) => {
        if (value === null) node.removeAttribute(name); else node.setAttribute(name, value);
      });
      entry.layers.forEach(({ node, value }) => { node.style.zIndex = value; });
      if (wasTop) restoreTarget = entry.opener;
      sync();
    },
  };
}
